/* eslint-disable max-lines */
import { useCallback, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  Archive,
  Check,
  Keyboard,
  Redo2,
  Replace,
  ReplaceAllIcon,
  RotateCcw,
  Save,
  Search,
  TimerIcon,
  Undo2
} from 'lucide-react';
import Input from '@components/atoms/ui-kit/input.tsx';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal.tsx';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';
import { useThemeVars } from '@utils/hooks/use-theme-vars.ts';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { useSharedCaption } from '@utils/providers/shared-caption-context.tsx';
import { useSharedCaptionEditorContext } from '@utils/providers/shared-caption-editor-context.tsx';
import { DocumentSubtitleSegmentV1 } from '@utils/types/editors/subtitles/subtitle.ts';
import SharedCaptionTimecode from '@components/editors/shared-editor/shared-caption/shared-caption-timecode.tsx';
import KeyboardShortcutsModal, {
  captionShortcutGroups
} from '@components/editors/keyboard-shortcuts-modal.tsx';
import { shortcutLabel } from '@utils/editors/shortcut-label.tsx';

type SharedCaptionToolbarProps = {
  setMatches: (matches: DocumentSubtitleSegmentV1[]) => void;
  isDocumentFinished: boolean;
};

export const SharedCaptionToolbar = ({
  setMatches,
  isDocumentFinished
}: SharedCaptionToolbarProps) => {
  const { t } = useTranslation();
  const { cls } = useThemeVars();
  const { getColour } = useThemeContext();

  // Shared caption context
  const {
    document: captionDocument,
    searchInputRef,
    search,
    setSearch,
    setCurrentMatch,
    updateCaptionDocument,
    wholeWord,
    setWholeWord,
    caseSensitive,
    setCaseSensitive,
    reset,
    saveDocument,
    saveDocumentLoading,
    documentResetting,
    cleanDocument,
    cleanDocumentLoading,
    undo: undoDoc,
    redo: redoDoc
  } = useSharedCaption();

  const { replaceAllWordsInEditor } = useSharedCaptionEditorContext();

  // Search state
  const [query, setQuery] = useState<string>('');
  const [replacement, setReplacement] = useState<string>('');
  const [replaceOpen, setReplaceOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Modal state
  const [modal, setModal] = useState<'' | 'reset' | 'clean' | 'timecode'>('');
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [resetingItems, setResetingItems] = useState(false);
  const [clearingItem, setClearingItem] = useState(false);

  const escapeRegex = (str: string) => {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const debounceSearch = useCallback(
    debounce((value: string) => {
      setSearch(value);
    }, 300),
    [setSearch]
  );

  const handleSearch = (value: string) => {
    setQuery(value);
    debounceSearch(value);
  };

  const findMatches = useCallback(
    (searchText: string, isCaseSensitive: boolean, isWholeWord: boolean) => {
      if (!searchText || !captionDocument) return [];

      try {
        const foundMatches: DocumentSubtitleSegmentV1[] = [];
        const escapedText = escapeRegex(searchText);
        const searchQuery = isWholeWord
          ? `(?:^|\\s)${escapedText}(?=$|\\s)`
          : escapedText;
        const regex = new RegExp(searchQuery, isCaseSensitive ? 'g' : 'gi');

        captionDocument.document.forEach(child => {
          if (child.text?.match(regex)) {
            foundMatches.push(child);
          }
        });

        return foundMatches;
      } catch {
        return [];
      }
    },
    [captionDocument?.document]
  );

  useEffect(() => {
    if (!search) {
      setMatches([]);
      return;
    }

    const foundMatches = findMatches(search, caseSensitive, wholeWord);
    setMatches(foundMatches);
    if (foundMatches.length === 0) return;
    setCurrentMatch(foundMatches);
  }, [search, captionDocument?.document, caseSensitive, wholeWord]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setQuery('');
        setSearch('');
        setMatches([]);
        setReplaceOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setSearch, setMatches]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setReplaceOpen(false);
      }
    };

    if (replaceOpen) {
      window.document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      window.document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [replaceOpen]);

  const handleReplaceAll = () => {
    if (!search || !replacement || !captionDocument) return;

    try {
      const escapedSearch = escapeRegex(search);
      const searchQuery = wholeWord
        ? `(?:^|\\s)${escapedSearch}(?=$|\\s)`
        : escapedSearch;
      const regex = new RegExp(searchQuery, caseSensitive ? 'g' : 'gi');

      const segments: DocumentSubtitleSegmentV1[] =
        captionDocument.document.map(child => ({
          ...child,
          text: child.text.replace(regex, replacement)
        }));

      updateCaptionDocument({ ...captionDocument, document: segments });
      replaceAllWordsInEditor(search, caseSensitive, wholeWord, replacement);
    } catch {
      // Silently handle errors
    }
  };

  const handleReset = async () => {
    setResetingItems(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    reset();
    setResetingItems(false);
    setModal('');
  };

  const handleClearDocument = async () => {
    setClearingItem(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    cleanDocument();
    setClearingItem(false);
    setModal('');
  };

  const onUndo = () => {
    undoDoc();
  };

  const onRedo = () => {
    redoDoc();
  };

  const iconBtnClass = cn(
    'flex items-center justify-center h-9 w-9 transition-colors hover:opacity-80 rounded-2xl border shrink-0 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:opacity-40',
    cls.cardBg
  );
  const iconBtnStyle = { borderColor: getColour('border') };
  const activeIconStyle = {
    borderColor: getColour('border'),
    backgroundColor: getColour('thirdLayer'),
    color: getColour('activeText')
  };

  return (
    <>
      <header
        className={cn(
          'flex items-center justify-between gap-2 px-3 py-1 h-14 shrink-0 border-b overflow-x-auto w-full',
          cls.text
        )}
        style={{ borderColor: getColour('border') }}
      >
        {/* Left cluster: history actions (mirrors transcript-body header) */}
        <div className="flex items-center gap-1 shrink-0">
          <Tooltip label={t('keyboard-shortcuts')}>
            <button
              type="button"
              onClick={() => setShortcutsOpen(true)}
              aria-label={t('keyboard-shortcuts')}
              className={iconBtnClass}
              style={iconBtnStyle}
            >
              <Keyboard
                size={14}
                style={{ color: getColour('icon') }}
                aria-hidden="true"
              />
            </button>
          </Tooltip>
          <Tooltip label={shortcutLabel(t('undo'), 'Ctrl+Z')}>
            <button
              type="button"
              onClick={onUndo}
              disabled={saveDocumentLoading}
              aria-label={t('undo')}
              className={iconBtnClass}
              style={iconBtnStyle}
            >
              <Undo2 size={14} style={{ color: getColour('icon') }} />
            </button>
          </Tooltip>
          <Tooltip label={shortcutLabel(t('redo'), 'Ctrl+Y')}>
            <button
              type="button"
              onClick={onRedo}
              disabled={saveDocumentLoading}
              aria-label={t('redo')}
              className={iconBtnClass}
              style={iconBtnStyle}
            >
              <Redo2 size={14} style={{ color: getColour('icon') }} />
            </button>
          </Tooltip>
          <Tooltip label={t('reset')}>
            <button
              type="button"
              onClick={() => setModal('reset')}
              disabled={
                saveDocumentLoading ||
                cleanDocumentLoading ||
                documentResetting ||
                isDocumentFinished
              }
              aria-label={t('reset')}
              className={iconBtnClass}
              style={iconBtnStyle}
            >
              <RotateCcw size={14} style={{ color: getColour('icon') }} />
            </button>
          </Tooltip>
          <Tooltip label={shortcutLabel(t('save'), 'Ctrl+S')}>
            <button
              type="button"
              onClick={() => saveDocument(true)}
              disabled={saveDocumentLoading || isDocumentFinished}
              aria-label={t('save')}
              className={iconBtnClass}
              style={iconBtnStyle}
            >
              <Save size={14} style={{ color: getColour('icon') }} />
            </button>
          </Tooltip>
          <Tooltip label={t('clean-up')}>
            <button
              type="button"
              onClick={() => setModal('clean')}
              disabled={
                saveDocumentLoading || cleanDocumentLoading || documentResetting
              }
              aria-label={t('clean-up')}
              className={iconBtnClass}
              style={iconBtnStyle}
            >
              <Archive size={14} style={{ color: getColour('icon') }} />
            </button>
          </Tooltip>
        </div>

        {/* Right cluster: timecodes + search / replace tools */}
        <div className="flex items-center gap-1 shrink-0" ref={dropdownRef}>
          <Tooltip label={t('timecodes')}>
            <button
              type="button"
              onClick={() => setModal('timecode')}
              aria-label={t('timecodes')}
              className={iconBtnClass}
              style={iconBtnStyle}
            >
              <TimerIcon size={14} style={{ color: getColour('icon') }} />
            </button>
          </Tooltip>

          {/* Search input */}
          <div className="relative">
            <div className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none">
              <Search size={14} style={{ color: getColour('icon') }} />
            </div>
            <Input
              ref={searchInputRef}
              placeholder={t('search')}
              onChange={e => handleSearch(e.target.value)}
              type="text"
              value={query}
              className="block h-8 text-xs pl-8"
            />
          </div>

          {/* Replace toggle */}
          <Tooltip label={t('replace')}>
            <button
              type="button"
              onClick={() => setReplaceOpen(!replaceOpen)}
              aria-label={t('replace')}
              className={iconBtnClass}
              style={replaceOpen ? activeIconStyle : iconBtnStyle}
            >
              <ReplaceAllIcon
                size={14}
                style={{
                  color: replaceOpen
                    ? getColour('activeText')
                    : getColour('icon')
                }}
              />
            </button>
          </Tooltip>

          {replaceOpen && (
            <>
              <div className="relative">
                <div className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none">
                  <Replace size={14} style={{ color: getColour('icon') }} />
                </div>
                <Input
                  placeholder={t('replace-with')}
                  onChange={e => setReplacement(e.target.value)}
                  type="text"
                  value={replacement}
                  className="block h-8 text-xs pl-8"
                  autoFocus
                />
              </div>
              <Tooltip label={t('case-sensitive')}>
                <button
                  type="button"
                  onClick={() => setCaseSensitive(!caseSensitive)}
                  aria-label={t('case-sensitive')}
                  className={iconBtnClass}
                  style={caseSensitive ? activeIconStyle : iconBtnStyle}
                >
                  <span
                    className="text-xs font-medium"
                    style={{
                      color: caseSensitive
                        ? getColour('activeText')
                        : getColour('normalText')
                    }}
                  >
                    Aa
                  </span>
                </button>
              </Tooltip>
              <Tooltip label={t('whole-word')}>
                <button
                  type="button"
                  onClick={() => setWholeWord(!wholeWord)}
                  aria-label={t('whole-word')}
                  className={iconBtnClass}
                  style={wholeWord ? activeIconStyle : iconBtnStyle}
                >
                  <span
                    className="text-xs font-medium"
                    style={{
                      color: wholeWord
                        ? getColour('activeText')
                        : getColour('normalText')
                    }}
                  >
                    Ww
                  </span>
                </button>
              </Tooltip>
              <Tooltip label={t('replace-all')}>
                <button
                  type="button"
                  onClick={handleReplaceAll}
                  disabled={!query || !replacement}
                  aria-label={t('replace-all')}
                  className={iconBtnClass}
                  style={iconBtnStyle}
                >
                  <Check size={14} style={{ color: getColour('icon') }} />
                </button>
              </Tooltip>
            </>
          )}
        </div>
      </header>

      <KeyboardShortcutsModal
        isOpen={shortcutsOpen}
        onClose={() => setShortcutsOpen(false)}
        groups={captionShortcutGroups(t)}
      />

      {/* Timecode Modal */}
      {modal === 'timecode' && (
        <SharedCaptionTimecode
          setTimeCodeOpen={value => setModal(!value ? '' : 'timecode')}
        />
      )}

      {/* Reset Confirmation Modal */}
      <ConfirmationModal
        isOpen={modal === 'reset'}
        onClose={() => setModal('')}
        onConfirm={handleReset}
        size="small"
        title={t('reset-confirmation')}
        caption={t('reset-confirmation-desc')}
        confirmLabel={resetingItems ? t('reseting') : t('reset')}
        cancelLabel={t('cancel')}
        confirmDisabled={resetingItems}
        destructive={false}
        autoFocusConfirm
      />

      {/* Clean Confirmation Modal */}
      <ConfirmationModal
        isOpen={modal === 'clean'}
        onClose={() => setModal('')}
        onConfirm={handleClearDocument}
        size="small"
        title={t('cleanup-confirmation')}
        caption={t('cleanup-confirmation-desc')}
        confirmLabel={clearingItem ? t('cleaning') : t('clean')}
        cancelLabel={t('cancel')}
        confirmDisabled={clearingItem}
        destructive={false}
        autoFocusConfirm
      />
    </>
  );
};
